/** The Pre-check-in tab's guest-form preview (plan §3 "Dock — Preview"): `GuestFormPreview`
 *  drawn from the DRAFT in edit mode — the stored settings in view mode — with the
 *  operator's options (its enabled waivers, its tag categories with their derived facts)
 *  and sample guests whose template values come from the variables the confirmation
 *  message may use. Client-side, no request: it follows every edit at once.
 *
 *  Its step is the page's (`usePreviewStep`): the section being worked in moves it. Below
 *  1100 px of content it is a 380 px drawer opened from the facts band. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { Box, Drawer, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useId, useMemo, useState } from 'react';

import type { OperatorPreCheckVariables } from '@/api/operatorPrecheck';
import { PreviewLimitsDisclosure, type PreviewLimit } from '@/components/PreviewLimits/PreviewLimits';
import { GuestFormPreview } from '@/features/precheck/preview/GuestFormPreview';
import { SAMPLE_GUESTS, type GuestPreviewOptions, type GuestPreviewSample } from '@/features/precheck/preview/previewModel';
import { useDraft, useDraftSummary } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { prefersReducedMotion } from '@/features/records/page/recordPage';

import { settingsAfter } from './compile';
import { useOperatorPrecheck, usePreviewStep } from './context';

/** The template variables a confirmation message may use (`{{ participant.… }}`): the
 *  variables tree flattened to paths, kept to the allowed patterns (`participant.*` is one
 *  segment under `participant`). */
export function templateVariables(v: OperatorPreCheckVariables): string[] {
  const out: string[] = [];
  const walk = (node: unknown, prefix: string) => {
    if (node === true) {
      if (prefix) out.push(prefix);
      return;
    }
    if (typeof node !== 'object' || node === null || Array.isArray(node)) return;
    for (const [k, child] of Object.entries(node as Record<string, unknown>)) walk(child, prefix ? `${prefix}.${k}` : k);
  };
  walk(v.waivers_variables ?? {}, '');
  const allowed = v.waivers_variables_allowed.map((p) => p.replace(/\.\*$/, ''));
  return out.filter((p) => allowed.some((a) => p.startsWith(`${a}.`) && !p.slice(a.length + 1).includes('.')));
}

/** A sample value for every variable: the sample guest's where it has one, else the
 *  variable's name in brackets ("[passport number]"). */
export function sampleVariables(v: OperatorPreCheckVariables): Record<string, string> {
  const out: Record<string, string> = {};
  for (const path of templateVariables(v)) {
    out[path] = SAMPLE_GUESTS.variables[path] ?? `[${(path.split('.').pop() ?? path).replace(/_/g, ' ')}]`;
  }
  return out;
}

export function PrecheckPreviewPane() {
  const { baseline, operatorName } = useOperatorPrecheck();
  const { mode } = useRecordEditor();
  const { state, baselineOf } = useDraft();
  const { count } = useDraftSummary();
  const { step, setStep } = usePreviewStep();
  const rec = baseline.rec;
  const stored = baseline.paths[rec] ?? {};
  // View mode shows the STORED settings (the record rule); edit mode the draft.
  const settings = useMemo(() => {
    const valueOf = (path: string) => {
      const e = mode === 'edit' ? state.entries[rec]?.[path] : undefined;
      return e ? e.to : (baselineOf(rec, path) ?? stored[path]);
    };
    return settingsAfter(valueOf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, state.version, rec, baselineOf, stored]);
  const { options } = baseline.data;
  const previewOptions = useMemo<GuestPreviewOptions>(
    () => ({
      categories: options.categories.map((c) => ({ code: c.code, name: c.name, required: c.required, multiselect: c.multiselect, tags: c.tags })),
      // Guests see enabled waivers only, in the operator's order.
      waivers: options.waivers
        .filter((w) => w.enabled)
        .map((w) => ({ id: w.id, name: w.name, languages: w.languages, html: w.html })),
    }),
    [options],
  );
  const sample = useMemo<GuestPreviewSample>(
    () => ({ ...SAMPLE_GUESTS, operatorName, variables: { ...SAMPLE_GUESTS.variables, ...sampleVariables(options.variables) } }),
    [operatorName, options.variables],
  );
  // What the frame shows, in words and an icon — as the tour's phone preview says it.
  const drafting = mode === 'edit' && count > 0;
  return (
    <Box data-precheck-preview sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        component="div"
        data-preview-status={drafting ? 'draft' : 'saved'}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, '& > svg': { fontSize: 18, flex: 'none' } }}
      >
        {drafting ? (
          <EditNoteOutlinedIcon aria-hidden sx={{ color: 'warning.dark' }} />
        ) : (
          <CheckCircleOutlineIcon aria-hidden sx={{ color: 'text.secondary' }} />
        )}
        {drafting ? 'Your unsaved draft' : 'The saved version'}
      </Typography>
      <GuestFormPreview settings={settings} options={previewOptions} step={step} onStepChange={setStep} sample={sample} />
      <PreviewLimitsDisclosure limits={PRECHECK_PREVIEW_LIMITS} />
    </Box>
  );
}

/** What the guest-form preview never shows, whatever the draft holds. */
export const PRECHECK_PREVIEW_LIMITS: readonly PreviewLimit[] = [
  { lead: 'Guests must complete it, Guests may edit after pre-check-in', reason: 'the form reads the same either way' },
  { lead: 'Party members join the chat', reason: 'chat membership is not part of the form' },
  {
    lead: 'Custom fields on other tours',
    reason: 'a relabel or a new type here reaches every tour that asks the field; the preview draws this form only',
  },
  { lead: 'Tours with their own settings', reason: 'they ask their own form — this is what the tours that inherit these settings show' },
  {
    lead: 'Tag-category questions',
    reason:
      'required and one or several choices come from the operator’s default tag categories — a tour with its own may differ; at most 100 tags of a category are drawn',
  },
  { lead: 'Waiver audiences', reason: 'the sample guests carry no tags, so every waiver is drawn' },
  { lead: 'Real guests', reason: 'sample guests only — typing in the frame sends nothing' },
];

export const PREVIEW_DRAWER_WIDTH = 380;

export function PrecheckPreviewDrawer({
  open,
  onClose,
  restoreFocus = true,
  onExited,
}: {
  open: boolean;
  onClose: () => void;
  /** Back to the opener on close (default); off when the page closes it (the opener is gone). */
  restoreFocus?: boolean;
  onExited?: () => void;
}) {
  const titleId = useId();
  // The pane stays through the slide out and goes when it has ended.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Above the app bar (zIndex.drawer + 1): a temporary drawer at zIndex.drawer sits
      // under it, its title and Close hidden (lessons: "Overlays that must clear the AppBar").
      sx={{ zIndex: (t) => t.zIndex.modal }}
      transitionDuration={prefersReducedMotion() ? 0 : undefined}
      disableRestoreFocus={!restoreFocus}
      SlideProps={{
        onExited: () => {
          setMounted(false);
          onExited?.();
        },
      }}
      PaperProps={{
        role: 'dialog',
        'aria-labelledby': titleId,
        sx: { width: PREVIEW_DRAWER_WIDTH, maxWidth: '100vw', display: 'flex', flexDirection: 'column' },
      }}
      data-preview-drawer
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.75, pr: 0.75, py: 0.75, borderBottom: 1, borderColor: 'divider', flex: 'none' }}>
        <Typography id={titleId} variant="subtitle2" component="h2" sx={{ m: 0, flex: 1 }}>
          Preview
        </Typography>
        <Tooltip title="Close (Esc)">
          <IconButton size="small" onClick={onClose} aria-label="Close the preview">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 1.75, py: 1.25 }}>{(open || mounted) && <PrecheckPreviewPane />}</Box>
    </Drawer>
  );
}
